import React from 'react';

import {
  Box,
  CircularProgress,
  IconButton,
  SelectChangeEvent,
  Switch,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import {
  Member,
  MemberRole,
  UpdateOrganizationMemberSchema,
} from 'src/Superadmin/index.types';
import { useOrganizationMember } from './hooks';
import { StyledFlex } from 'src/components/StyledFlex';
import { useNavigate } from 'react-router-dom';
import { StyledActionCell, stickyNameCellSx } from './styles';
import { EditSharp } from '@mui/icons-material';
import { BranchTagWithCountry } from 'src/utils/branches';
import { client } from 'src/utils/api-client';
import PermissionSelect from '../components/PermissionSelect';
import { OrganizationTag } from 'src/utils/tags';
import { CountryRollup } from 'src/Superadmin/components/CountryGroupHeader';

// -- C13 country grouping (specs/001-raben-country-masteradmin/mass-specs/
// specs/20260826-fe-users-country.md) -------------------------------------
// These live here, rather than in a new shared module, because Row.tsx is
// the one file every row/table shell in this feature already imports from
// (OptimizedUsersTable.tsx, the legacy UsersTable/index.tsx, and
// SupplierRow.tsx) -- C13's file scope has no separate shared-util file, so
// this is where a single grouping both tables can safely reuse lives.

// Sentinel key for the "Bez kraju" bucket -- branches with no country yet
// (C04 rollout bucket). Mirrors Structure's own NO_COUNTRY_VALUE convention
// (StructureTable.tsx) for the same concept, kept as a separate constant
// here since importing across feature folders for one string is not worth
// the coupling.
export const NO_COUNTRY_GROUP_KEY = 'none';

export interface CountryBranchGroup {
  key: string;
  label: string;
  // null => the "Bez kraju" bucket, which never gets a master switch: there
  // is no real `organizations.Country` row to grant via C07's
  // PUT .../countries, only a real country code can be granted.
  countryCode: string | null;
  branches: BranchTagWithCountry[];
}

// Shared by both tables (Goal 1/3/4): reorders branches so every country's
// columns are contiguous (first-seen country order, "Bez kraju" trailing),
// and -- when `selectedCountry` is set -- narrows to just that country's
// branches ("narrows ... visible columns"). Safe to reorder/narrow for
// EITHER table because both Row.tsx and SupplierRow.tsx take this exact
// computed array as a `groups` prop rather than fetching branches
// themselves -- the header and the switches under it are always built from
// the same source, so they can never disagree on order or width.
export function groupBranchesByCountry(
  branches: BranchTagWithCountry[],
  noCountryLabel: string,
  selectedCountry?: string
): CountryBranchGroup[] {
  const scoped = selectedCountry
    ? branches.filter((branch) => branch.country_code === selectedCountry)
    : branches;

  const groups: CountryBranchGroup[] = [];
  const indexByCode = new Map<string, number>();
  let noCountryGroup: CountryBranchGroup | null = null;

  scoped.forEach((branch) => {
    if (!branch.country_code) {
      if (!noCountryGroup) {
        noCountryGroup = {
          key: NO_COUNTRY_GROUP_KEY,
          label: noCountryLabel,
          countryCode: null,
          branches: [],
        };
      }
      noCountryGroup.branches.push(branch);
      return;
    }

    let index = indexByCode.get(branch.country_code);
    if (index === undefined) {
      index = groups.length;
      indexByCode.set(branch.country_code, index);
      groups.push({
        key: branch.country_code,
        label: branch.country_name || branch.country_code,
        countryCode: branch.country_code,
        branches: [],
      });
    }
    groups[index].branches.push(branch);
  });

  if (noCountryGroup) groups.push(noCountryGroup);
  return groups;
}

// Suppliers (Goal 4) used to need a second, order-preserving grouping here
// because SupplierRow.tsx fetched its OWN branch list independently. Now
// that SupplierRow also takes the table shell's `groups` as a prop (same as
// this Row), both tables share ONE grouping -- `groupBranchesByCountry`
// above -- with no risk of the header and the switches under it disagreeing
// on order. See OptimizedUsersTable.tsx.

interface Props {
  // `countries` / `full_countries` (C07, settings_panel/api_v2.py
  // SubscribedUserSchema) are on the shared Member type (index.types.tsx).
  member: Member;
  // Pre-grouped, already-filtered branch columns from the table shell
  // (OptimizedUsersTable.tsx or UsersTable/index.tsx) -- kept as a prop,
  // never fetched here, so this row's cells always match whatever columns
  // the header actually rendered (mirrors Structure's Row.tsx).
  groups: CountryBranchGroup[];
  // specs/.../20260826-fe-country-group-folding.md: resolves a group's
  // collapse state, computed once in the table shell (shared with the
  // header rows and, for the Users tables, with SupplierRow -- Goal 4) so
  // this row can never disagree with what's rendered above it.
  isGroupOpen: (key: string) => boolean;
}
export default function Row(props: Props) {
  const { member, groups, isGroupOpen } = props;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const organizationMember = member.organizations.find((organization) =>
    organization.organization_tags.includes(OrganizationTag.RabenRoot)
  )!;

  const { updateUserBranches, updateOrganizationMember } =
    useOrganizationMember(member.id);

  const fullName =
    member.first_name || member.last_name
      ? `${member.first_name} ${member.last_name}`
      : member.email;

  const memberCountries = member.countries || [];
  const memberFullCountries = member.full_countries || [];

  // C07: PUT /web/settings_v2/users/{id}/countries. Defined inline (rather
  // than added to ./hooks, which is outside C13's file scope) -- it
  // invalidates the exact same query keys UsersTable/hooks.ts's own branch
  // mutations already invalidate, so both tables (which key their queries
  // off the 'organization-members-paginated' prefix -- see
  // OptimizedUsersTable.tsx) refetch and re-derive this switch's state from
  // the server, same as every other mutation on this screen (EC-1).
  const updateUserCountry = useMutation({
    mutationFn: ({ country, value }: { country: string; value: boolean }) =>
      client.put(`web/settings_v2/users/${member.id}/countries`, {
        country,
        value,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries(['organization-members-paginated']);
      queryClient.invalidateQueries(['organization-members-legacy']);
      queryClient.invalidateQueries('organization-members');
      toast.success(
        t(
          'superadmin.settings.tabs.users.usersTable.toasts.success.update'
        ) as string
      );
    },
  });

  const updateBranch = (branch: string, value: boolean) =>
    updateUserBranches.mutate({ branch, value });

  const updateCountry = (countryCode: string, value: boolean) =>
    updateUserCountry.mutate({ country: countryCode, value });

  const updateMember = (extras: Partial<UpdateOrganizationMemberSchema>) =>
    updateOrganizationMember.mutate({
      ...organizationMember,
      organizationId: organizationMember.organization_id,
      ...extras,
    });

  return (
    <TableRow sx={{ p: 0 }}>
      <TableCell
        sx={{
          ...stickyNameCellSx,
          verticalAlign: 'top',
          minWidth: '400px',
          p: '8px',
        }}
      >
        <StyledFlex sx={{ alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="h6">{fullName}</Typography>
            <Typography variant="captionText" component="div">
              {member.email}
            </Typography>
          </Box>
          <IconButton
            onClick={() => navigate(`/superadmin/users/${member.id}`)}
          >
            <EditSharp sx={{ color: '#6E6E6E' }} />
          </IconButton>
        </StyledFlex>
      </TableCell>
      <TableCell>
        <PermissionSelect
          key={organizationMember.id}
          value={organizationMember.role}
          onChange={(e: SelectChangeEvent<any>) =>
            updateMember({ role: e.target.value as MemberRole })
          }
          disabled={updateOrganizationMember.isLoading}
        />
      </TableCell>
      {groups.map((group) => (
        <React.Fragment key={group.key}>
          {group.countryCode && (
            <StyledActionCell>
              {/* Goal 1/2: per-row master switch, "cały kraj" -- ON means a
                  real full_country grant (C07), never a summary of "every
                  current branch happens to be on". OFF-with-tooltip when the
                  user holds some but not all of this country's branches
                  (EC-1: after removing one branch, C07 BR-1 drops the grant
                  server-side and this re-derives to off-with-tooltip on the
                  next refetch). */}
              <Tooltip
                title={
                  !memberFullCountries.includes(group.countryCode) &&
                  memberCountries.includes(group.countryCode)
                    ? String(
                        t(
                          'superadmin.settings.tabs.users.usersTable.tooltips.partialCountryAccess'
                        )
                      )
                    : ''
                }
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                  }}
                >
                  <Switch
                    checked={memberFullCountries.includes(group.countryCode)}
                    onChange={(e) =>
                      updateCountry(
                        group.countryCode as string,
                        e.target.checked
                      )
                    }
                    disabled={updateUserCountry.isLoading}
                    aria-label={`Toggle full ${group.label} access for ${fullName}`}
                    sx={{
                      opacity: updateUserCountry.isLoading ? 0.6 : 1,
                      transition: 'opacity 0.2s ease-in-out',
                    }}
                  />
                  {updateUserCountry.isLoading && (
                    <CircularProgress
                      size={16}
                      sx={{ color: 'primary.main' }}
                    />
                  )}
                </Box>
              </Tooltip>
            </StyledActionCell>
          )}
          {isGroupOpen(group.key) ? (
            group.branches.map((branch) => (
              <StyledActionCell key={branch.name}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Switch
                    checked={member.branches.includes(branch.name)}
                    onChange={(e) =>
                      updateBranch(branch.name, e.target.checked)
                    }
                    disabled={updateUserBranches.isLoading}
                    aria-label={`Toggle ${
                      branch.description || branch.name
                    } access for ${fullName}`}
                    sx={{
                      opacity: updateUserBranches.isLoading ? 0.6 : 1,
                      transition: 'opacity 0.2s ease-in-out',
                    }}
                  />
                  {updateUserBranches.isLoading && (
                    <CircularProgress
                      size={16}
                      sx={{ color: 'primary.main' }}
                    />
                  )}
                </Box>
              </StyledActionCell>
            ))
          ) : (
            // Goal 2/BR-2/BR-3/EC-2/EC-7: the group's BRANCH switches
            // collapse to a roll-up -- BR-1 (above) keeps the "cały kraj"
            // master switch itself outside this branch entirely, always
            // rendered regardless of isGroupOpen.
            <StyledActionCell key={`${group.key}-rollup`}>
              <CountryRollup
                on={
                  group.branches.filter((branch) =>
                    member.branches.includes(branch.name)
                  ).length
                }
                total={group.branches.length}
                ariaLabel={String(
                  t('superadmin.settings.countryFolding.rollupAriaLabel', {
                    on: group.branches.filter((branch) =>
                      member.branches.includes(branch.name)
                    ).length,
                    total: group.branches.length,
                  })
                )}
              />
            </StyledActionCell>
          )}
        </React.Fragment>
      ))}
    </TableRow>
  );
}
